import { createContext, ReactElement, useReducer } from "react"
import axios from "utils/axios"
import { FETCH_AFFILIATES, FETCH_AFFILIATE_RELATIONSHIPS, FETCH_ANALYTICS_SCOUT_LOGS, FETCH_AFFILIATE_PAYMENTS } from "../store/reducers/actions"
import { mapPaginatedDataToCamelCase } from "../utils/caseTransformation"
import { PaginatedData } from "types/root"
import { Affiliate, AffiliateFilterType, AffiliatePayments, AffiliateRelationship, AffiliatesContextType } from "types/affiliates"
import affiliatesReducer, { initialAffiliateState } from "store/reducers/affiliates"
import { getFilterText, toastMessage } from "utils"

const AffiliatesContext = createContext<AffiliatesContextType | null>(null)

export const AffiliatesProvider = ({ children }: { children: ReactElement }) => {
  const [state, dispatch] = useReducer(affiliatesReducer, initialAffiliateState)

  const buildAffiliateParams = (filter: AffiliateFilterType, orderBy?: string) => {
    const searchParam = `${filter.search ? `&search=${filter.search}` : ""}`
    const identifierParam = `${filter.identifier ? `&identifier=${filter.identifier}` : ""}`
    const platformParam = `${filter.platform ? `&platform=${filter.platform}` : ""}`
    const assignedUsersParam = filter.assignedUsers?.length
      ? `&assigned_users=${filter.assignedUsers?.map((option) => option.value)?.join(",") || ""}`
      : ""
    const assignedAffiliateUsersParam = filter.assignedAffiliateUsers?.length
      ? `&assigned_affiliate_users=${filter.assignedAffiliateUsers?.map((option) => option.value)?.join(",") || ""}`
      : ""
    const assignedAffiliateParam = filter.assignedAffiliate ? `&assigned_affiliate=${filter.assignedAffiliate}` : ""
    const relStatusParam = filter.relStatus ? `&rel_status=${filter.relStatus}` : ""
    const priorityParam = `${filter.priority ? `&priority=${filter.priority}` : ""}`
    const isAffiliateParam = `${filter.isAffiliate ? `&is_affiliate=${filter.isAffiliate}` : ""}`
    const isArtistParam = `${filter.isArtist ? `&is_artist=${filter.isArtist}` : ""}`
    const contactStatusParam = `${filter.contactStatus ? `&contact_status=${filter.contactStatus}` : ""}`
    const orderByParam = `${orderBy ? `&order_by=${orderBy}` : ""}`
    const pipelineActionIdList = filter.pipelineAction?.map((option) => option.value)?.join(",") || ""
    const clickupTaskFilterParam =
      filter.clickup?.value !== 0 && filter.clickup?.value !== undefined
        ? `&clickup_task_filter=${filter.clickup?.value2}`
        : ""
    const idParam = `${filter.id ? `&id=${filter.id}` : ""}`
    const countryParam = `${filter.country ? `&country=${filter.country}` : ""}`
    const artistCountryParam = `${filter.artistCountry ? `&artist_country=${filter.artistCountry}` : ""}`
    const followerArtistCountryParam = `${
      filter.followerArtistCountry ? `&follower_artist_country=${filter.followerArtistCountry}` : ""
    }`
    const hideParam = `${filter.hide ? `&hide=1` : ""}`
    const followingParam = `${
      filter.following && !(filter.following[0] === 0 && filter.following[1] === 10000000000)
        ? `&following=${filter.following.join(",")}`
        : ""
    }`
    const followerParam = `${
      filter.follower && !(filter.follower[0] === 0 && filter.follower[1] === 10000000000)
        ? `&follower=${filter.follower.join(",")}`
        : ""
    }`

    const minSpMonthlyListenerParam = `${
      filter.minSpMonthlyListener ? `&min_sp_monthly_listener=${filter.minSpMonthlyListener}` : ""
    }`

    const followerHotlistParam = `${
      filter.followerHotlist && !(filter.followerHotlist[0] === 0 && filter.followerHotlist[1] === 10000000000)
        ? `&follower_hotlist=${filter.followerHotlist.join(",")}`
        : ""
    }`

    return `&pipeline_action=${pipelineActionIdList}${clickupTaskFilterParam}${searchParam}${identifierParam}${platformParam}${orderByParam}${assignedUsersParam}${assignedAffiliateUsersParam}${assignedAffiliateParam}${relStatusParam}${priorityParam}${isAffiliateParam}${idParam}${artistCountryParam}${followerArtistCountryParam}${hideParam}${countryParam}${followingParam}${followerParam}${isArtistParam}${contactStatusParam}${minSpMonthlyListenerParam}${followerHotlistParam}`
  }

  const fetchAffiliateList = async (limit: number, offset: number, filter: AffiliateFilterType, orderBy?: string) => {
    const response = await axios(
      `/affiliates/?limit=${limit}&offset=${offset}${buildAffiliateParams(filter, orderBy)}`,
      {
        method: "get"
      }
    )

    dispatch({
      type: FETCH_AFFILIATES,
      payload: {
        ...state,
        paginatedAffiliateList: mapPaginatedDataToCamelCase<PaginatedData<Affiliate>>(response.data)
      }
    })
  }

  const fetchAffiliateTable = async (limit: number, offset: number, filter: AffiliateFilterType, orderBy?: string) => {
    const response = await axios(
      `/affiliates/table/?limit=${limit}&offset=${offset}${buildAffiliateParams(filter, orderBy)}`,
      {
        method: "get"
      }
    )

    dispatch({
      type: FETCH_AFFILIATES,
      payload: {
        ...state,
        paginatedAffiliateList: mapPaginatedDataToCamelCase<PaginatedData<Affiliate>>(response.data)
      }
    })
  }

  const fetchAffiliateRelationshipList = async (
    limit: number,
    offset: number,
    orderBy?: string,
    affiliateId?: number,
    followedById?: number,
    search?: string,
    artistCountry?: string,
    showAssignedOnly?: boolean
  ) => {
    const affiliateIdParam = affiliateId ? `&affiliate_id=${affiliateId}` : ""
    const followedByIdParam = followedById ? `&followed_by_id=${followedById}` : ""
    const searchParam = search ? `&search=${search}` : ""
    const artistCountryParam = artistCountry ? `&artist_country=${artistCountry}` : ""
    const showAssignedOnlyParam = showAssignedOnly ? `&show_assigned_only=1` : ""

    const orderByParam = `${orderBy ? `&order_by=${orderBy}` : ""}`
    const response = await axios(
      `/affiliate/relationships/?limit=${limit}&offset=${offset}${followedByIdParam}${affiliateIdParam}${searchParam}${orderByParam}${artistCountryParam}${showAssignedOnlyParam}`,
      {
        method: "get"
      }
    )

    dispatch({
      type: FETCH_AFFILIATE_RELATIONSHIPS,
      payload: {
        ...state,
        paginatedAffiliateRelationshipList: mapPaginatedDataToCamelCase<PaginatedData<AffiliateRelationship>>(
          response.data
        )
      }
    })
  }

  const updateAffiliateRelationshipStatus = async (
    affiliateRel: AffiliateRelationship,
    status: boolean | null,
    inProgress: boolean,
    artistId?: number
  ) => {
    // TODO: This function needs changes, do not use this.
    const response = await axios(`/affiliate/relationships/update_status/`, {
      method: "put",
      data: {
        id: affiliateRel.id,
        artist_id: artistId,
        status,
        in_progress: inProgress,
        rel_status_update_affiliate_id: affiliateRel.followedById
      }
    })

    if (response.data.success) {
      toastMessage("Successfully updated status", "success")
    } else {
      toastMessage("Error updating status", "error")
      return
    }

    const paginatedAffiliateListResult = state.paginatedAffiliateList.results.map((affiliate) => {
      if (affiliate.affiliateRelationship?.id === affiliateRel.id) {
        if (inProgress && status !== null) {
          return { ...affiliate, affiliateRelationship: { ...affiliate.affiliateRelationship, inProgress: status } }
        } else {
          return { ...affiliate, affiliateRelationship: { ...affiliate.affiliateRelationship, status } }
        }
      }
      return { ...affiliate }
    })

    dispatch({
      type: FETCH_AFFILIATES,
      payload: {
        ...state,
        paginatedAffiliateList: { ...state.paginatedAffiliateList, results: [...paginatedAffiliateListResult] }
      }
    })
  }

  const fetchAnalyticsScoutLogs = async (limit: number, offset: number, filter?: any, orderBy?: string) => {
    const response = await axios(
      `/affiliate/relationships/analytics/?limit=${limit}&offset=${offset}${getFilterText(filter)}${
        orderBy ? `&order_by=${orderBy}` : ""
      }`,
      { method: "get" }
    )

    dispatch({
      type: FETCH_ANALYTICS_SCOUT_LOGS,
      payload: {
        ...state,
        analyticsScoutLogs: mapPaginatedDataToCamelCase<PaginatedData<Affiliate>>(response.data)
      }
    })
  }


  const fetchAffiliatePayments = async (limit: number, offset: number, filter?: any, orderBy?: string) => {
    const response = await axios(
      `/affiliate/deal_terms/fetch_affiliate_payments/?limit=${limit}&offset=${offset}${getFilterText(filter)}${
        orderBy ? `&order_by=${orderBy}` : ""
      }`,
      { method: "get" }
    )

        dispatch({
          type: FETCH_AFFILIATE_PAYMENTS,
          payload: {
            ...state,
            paginatedAffiliatePayments: mapPaginatedDataToCamelCase<PaginatedData<AffiliatePayments>>(response.data)
          }
        })
  }

  return (
    <AffiliatesContext.Provider
      value={{
        ...state,
        fetchAffiliateList,
        fetchAffiliateTable,
        fetchAffiliateRelationshipList,
        updateAffiliateRelationshipStatus,
        fetchAnalyticsScoutLogs,
        fetchAffiliatePayments
      }}
    >
      {children}
    </AffiliatesContext.Provider>
  )
}

export default AffiliatesContext
